import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  FileText, Package, UtensilsCrossed, Users,
  Settings as SettingsIcon, Menu as MenuIcon, X, ScanLine, BookOpen, Sun, LogOut, ChevronsLeft, ChevronsRight,
  AlertTriangle, Shield, LogOut as LogOutIcon,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api, http } from "../lib/api";
import AdvisorChat from "./AdvisorChat";
import TrialBanner from "./TrialBanner";
import ScanTasksIndicator from "./ScanTasksIndicator";
import { useAuth } from "../contexts/AuthContext";

const NAV = [
  { to: "/aujourdhui", label: "Tableau de bord", icon: Sun, id: "today" },
  { to: "/factures", label: "Factures", icon: FileText, id: "invoices" },
  { to: "/produits", label: "Produits", icon: Package, id: "products" },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Users, id: "suppliers" },
  { to: "/plats", label: "Fiches recettes", icon: UtensilsCrossed, id: "recipes" },
  { to: "/menus", label: "Menu", icon: BookOpen, id: "menu" },
  { to: "/parametres", label: "Paramètres", icon: SettingsIcon, id: "settings" },
];

import { Wordmark as BrandWordmark } from "./Brand";

function Wordmark({ compact = false }) {
  if (compact) {
    return (
      <div className="flex items-center justify-center rounded-md bg-[#FF9F1C] w-8 h-8 shrink-0" aria-label="RentaScan">
        <span className="text-[#0B1F33] font-extrabold text-[11px] tracking-[0.02em] leading-none">RS</span>
      </div>
    );
  }
  return <BrandWordmark tone="dark" size="lg" />;
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [establishment, setEstablishment] = useState("Le Petit Bistro");
  const [dishAlerts, setDishAlerts] = useState({ unread: 0, alerts: [] });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    api.settings().then((s) => setEstablishment(s.establishment_name || user?.establishment_name || "Le Petit Bistro")).catch(() => {
      if (user?.establishment_name) setEstablishment(user.establishment_name);
    });
  }, [user]);

  // Poll dish-flip alerts. Also refresh when route changes so newly-validated
  // invoices update the badge quickly.
  const refreshAlerts = () => api.dishFlips(true).then(setDishAlerts).catch(() => {});
  useEffect(() => { refreshAlerts(); }, [location.pathname]);
  useEffect(() => {
    const t = setInterval(refreshAlerts, 60000);
    return () => clearInterval(t);
  }, []);

  // Clear the badge (mark read) as soon as the user reaches /plats.
  useEffect(() => {
    if (location.pathname.startsWith("/plats") && dishAlerts.unread > 0) {
      api.readAllDishFlips().then(() => setDishAlerts({ unread: 0, alerts: [] })).catch(() => {});
    }
  }, [location.pathname, dishAlerts.unread]);

  const loadDemo = async () => {
    setLoading(true);
    try {
      await api.loadDemo();
      toast.success("Démo chargée", { description: "Factures 3 mois + hausse coppa +16 % en septembre." });
      window.location.reload();
    } catch { toast.error("Erreur lors du chargement de la démo"); }
    finally { setLoading(false); }
  };

  const asideWidth = collapsed ? "w-[76px]" : "w-[248px]";

  return (
    <div className="flex h-screen bg-[#F4F5EF] text-[#111] app-shell">
      <aside
        data-testid="sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:relative z-40 h-full ${asideWidth} bg-[#0B1F33] text-white flex flex-col transition-all duration-200`}
      >
        <div className="relative px-4 pt-5 pb-4 flex items-center gap-3">
          <Wordmark compact={collapsed} />
          <button onClick={() => setCollapsed((v) => !v)} data-testid="sidebar-collapse" aria-label="Réduire"
            className="hidden lg:flex ml-auto w-7 h-7 items-center justify-center rounded-md text-white/50 hover:text-white hover:bg-white/8 transition-colors">
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        </div>
        {!collapsed && (
          <div className="px-4 pb-4 mt-1">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#FFD166] font-bold">Restaurant</div>
            <div className="mt-1 text-[13px] font-semibold text-white truncate">{establishment}</div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto no-scrollbar px-3 pb-4">
          {NAV.map((item) => {
            const isPlats = item.id === "recipes";
            const badge = isPlats && dishAlerts.unread > 0 ? dishAlerts.unread : 0;
            const tooltipTitle = badge
              ? `${badge} plat${badge > 1 ? "s" : ""} sous objectif après la dernière facture validée`
              : item.label;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/aujourdhui"}
                data-testid={`sidebar-link-${item.id}`}
                onClick={() => setOpen(false)}
                title={tooltipTitle}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 my-0.5 text-[14px] rounded-lg transition-all ${
                    isActive
                      ? "bg-[#2878FF] text-white font-bold"
                      : "text-white/70 hover:bg-white/6 hover:text-white font-medium"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute -left-3 top-2 bottom-2 w-1 rounded-r-full bg-[#FF9F1C]" />}
                    <div className="relative shrink-0">
                      <item.icon className="w-[18px] h-[18px]" strokeWidth={2} />
                      {badge > 0 && collapsed && (
                        <span
                          data-testid="sidebar-plats-dot"
                          className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#FF9F1C] border-2 border-[#0B1F33] animate-pulse"
                          aria-hidden
                        />
                      )}
                    </div>
                    {!collapsed && (
                      <span className="flex-1 flex items-center gap-2">
                        {item.label}
                        {badge > 0 && (
                          <span
                            data-testid="sidebar-plats-alert"
                            className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#FF9F1C] text-[#0B1F33] text-[10.5px] font-extrabold font-mono-num animate-pulse"
                            title={`${badge} plat${badge > 1 ? "s" : ""} sous objectif`}
                          >
                            <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
                            {badge}
                          </span>
                        )}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {!collapsed && (
          <div className="p-3 border-t border-white/8 space-y-2">
            <Button
              data-testid="demo-data-load-btn"
              onClick={loadDemo}
              disabled={loading}
              className="w-full bg-[#FF9F1C] hover:bg-[#F58F06] text-[#0B1F33] rounded-lg text-[13.5px] h-10 font-bold shadow-none border-0"
            >
              {loading ? "Chargement…" : "Charger la démo"}
            </Button>
            <Button
              data-testid="seed-photos-btn"
              onClick={async () => {
                try {
                  const r = await api.seedDemoPhotos();
                  toast.success(`${r.updated} photos ajoutées sur ${r.total} recettes`);
                } catch { toast.error("Échec chargement photos"); }
              }}
              variant="ghost"
              className="w-full rounded-lg text-[12px] h-9 text-white/70 hover:text-white hover:bg-white/8 border border-white/15"
            >
              Photos démo
            </Button>
            {user && (
              <button
                data-testid="sidebar-logout-btn"
                onClick={async () => { await logout(); navigate("/"); }}
                className="w-full rounded-lg text-[12px] h-9 text-white/70 hover:text-white hover:bg-white/8 inline-flex items-center justify-center gap-1.5 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" /> Déconnexion
              </button>
            )}
          </div>
        )}
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <SimulationBanner />
        <TrialBanner />
        <header className="glass px-5 lg:px-8 h-16 flex items-center justify-between shrink-0 sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <button className="lg:hidden text-[#111]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#FF9F1C]" />
              <div className="font-bold text-[14px] text-[#111]">{establishment}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SimulationHeaderBadge />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6 lg:p-10 print-area">
          <div className="max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
      <AdvisorChat />
      <ScanTasksIndicator />
    </div>
  );
}

/* Compact header badge — cliquable, uniquement visible en mode simulation.
   Un clic rouvre le simulateur d'abonnement. Bien plus visible et pratique
   que la simple bannière quand on est loin de l'en-tête de page. */
function SimulationHeaderBadge() {
  const [plan, setPlan] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    http.get("/plan/me").then((r) => { if (!cancelled) setPlan(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);
  if (!plan?.simulated) return null;
  const label = plan.effective_plan.charAt(0).toUpperCase() + plan.effective_plan.slice(1);
  return (
    <button
      type="button"
      data-testid="header-sim-badge"
      title="Ouvrir le simulateur d'abonnement"
      onClick={() => navigate("/admin/simulateur")}
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#0B1F33] hover:bg-[#0B1F33]/85 text-white text-[12px] font-extrabold uppercase tracking-widest transition-colors"
    >
      <Shield className="w-3.5 h-3.5 text-[#FFD166]" />
      <span className="hidden sm:inline">Mode test :</span>
      <span>{label}</span>
    </button>
  );
}

/* --- Bandeau "Mode simulation · <Plan>" — visible uniquement pour un super_admin
   qui a activé une simulation via /admin/simulateur. Se met à jour à chaque
   navigation et propose un raccourci pour quitter la simulation. --- */
function SimulationBanner() {
  const [plan, setPlan] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const refresh = () => http.get("/plan/me").then((r) => setPlan(r.data)).catch(() => setPlan(null));
  useEffect(() => { refresh(); }, [location.pathname]);
  useEffect(() => {
    const onValidated = () => refresh();
    window.addEventListener("rentascan:invoice-validated", onValidated);
    return () => window.removeEventListener("rentascan:invoice-validated", onValidated);
  }, []);
  if (!plan?.simulated) return null;
  const label = plan.effective_plan.charAt(0).toUpperCase() + plan.effective_plan.slice(1);
  const exitSim = async () => {
    try {
      await http.post("/admin/simulate/reset");
      await refresh();
      toast.success("Simulation quittée");
    } catch { toast.error("Impossible de quitter la simulation"); }
  };
  return (
    <div
      data-testid="sim-banner"
      className="bg-[#FFF0D2] border-b border-[#FFD166] px-4 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-[#7A4C0B]"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Shield className="w-4 h-4 text-[#FF9F1C] shrink-0" />
        <span className="font-bold">Mode simulation · {label}</span>
        <span className="hidden sm:inline text-[#7A4C0B]/75">
          Les quotas et l'accès aux fonctions Négociation / Traduction reflètent cette formule, votre abonnement réel n'est pas modifié.
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          data-testid="sim-banner-open"
          onClick={() => navigate("/admin/simulateur")}
          className="h-8 px-3 rounded-md bg-white border border-[#FFD166] hover:border-[#FF9F1C] text-[12px] font-bold text-[#0B1F33]"
        >
          Ouvrir le simulateur
        </button>
        <button
          type="button"
          data-testid="sim-banner-exit"
          onClick={exitSim}
          className="h-8 px-3 rounded-md bg-[#0B1F33] hover:bg-[#0B1F33]/90 text-[12px] font-bold text-white inline-flex items-center gap-1.5"
        >
          <LogOutIcon className="w-3.5 h-3.5" /> Quitter
        </button>
      </div>
    </div>
  );
}
